import moment from "moment";
import "./RecentInvoices.css";
import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import MemberName from "./elements/memberName";
import { useSelector, useDispatch } from "react-redux";
import { ThreeDots } from 'react-loader-spinner';
import { renderCTA } from "../../helpers/invoiceHelpers";
import { useFetchRecentInvoices } from "./DashboardCurrencyApi";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import PaymentStatusWise from "../Invoices/elements/paymentStatusWise.jsx";
import { motion } from "framer-motion";
import { setAppLoading } from "../../features/appSettings/appSettingsSlice.js";
import tableArrow from "../../images/Notification-images/table-arrow.svg";
import { ReactComponent as ItemIcon } from "../../images/icons/itemIcon.svg";

const SortIcon = ({ field, sortField, sortOrder }) => {
  const isActive = sortField === field;
  return (
    <span
      style={{
        display: "inline-block",
        width: "16px",
        height: "16px",
        marginLeft: "6px",
        verticalAlign: "middle",
        backgroundImage: `url(${tableArrow})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "contain",
        opacity: isActive ? 1 : 0.45,
        transform: isActive && sortOrder === -1 ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease, opacity 0.2s ease",
        flexShrink: 0,
        cursor: "pointer",
      }}
    />
  );
};

const MobileSortIcon = () => (
  <span className="mobile-sort-icon-custom sort-icon-wrap" style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px', alignItems: 'center', justifyContent: 'center', marginLeft: '6px', verticalAlign: 'middle', lineHeight: 1, flexShrink: 0 }}>
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', color: '#405BFF', opacity: 1 }}>
      <path d="M1 4L4 1L7 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', color: '#405BFF', opacity: 1 }}>
      <path d="M1 1L4 4L7 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const sortableThStyle = { cursor: "pointer", userSelect: "none" };

const RecentInvoices = (props) => {
  const currencySum = props.sum;
  const dispatch = useDispatch();
  const dashboradDate = useSelector((state) => state.invoice.dashboardDateRange);
  const dashboradCurrency = useSelector((state) => state.invoice.dashboardCurrency);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const { appSettings } = useSelector((state) => state.appSettings);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 767);
  const timeTableRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortField(field);
      setSortOrder(1);
    }
  };

  const dateRange = useMemo(() => {
    const start = moment(dashboradDate[0]).tz(global.config.getTimeZone()).startOf("day").toISOString();
    const end = moment(dashboradDate[1]).tz(global.config.getTimeZone()).endOf("day").toISOString();
    return { start, end };
  }, [dashboradDate]);

  const {
    data: products = [],
    isLoading,
    isFetching,
    refetch: refetchRecentInvoice,
  } = useFetchRecentInvoices(
    token,
    dateRange.start,
    dateRange.end,
    100,
    dashboradCurrency,
    currencySum
  );

  const displayedProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    const base = products.slice(0, 5);
    if (!sortField) return base;
    const copy = [...base];
    copy.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "inv_number":
          return sortOrder * (a.inv_number || "").localeCompare(b.inv_number || "");
        case "inv_due_date":
          aVal = new Date(a.inv_due_date).getTime() || 0;
          bVal = new Date(b.inv_due_date).getTime() || 0;
          return sortOrder * (aVal - bVal);
        case "inv_total_amount":
          return sortOrder * ((a.inv_total_amount || 0) - (b.inv_total_amount || 0));
        case "inv_payment_status":
          return sortOrder * (a.inv_payment_status || "").localeCompare(b.inv_payment_status || "");
        default:
          return 0;
      }
    });
    return copy;
  }, [products, sortField, sortOrder]);

  useEffect(() => {
    const tableEl = timeTableRef.current;
    const contentOuter = document.querySelector(".content-outer.Dashboard:not(.clients):not(.vendors)");
    if (!isMobile || !tableEl || !contentOuter) {
      contentOuter?.classList.remove("dashboard-hscroll-end");
      return undefined;
    }

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 767) {
        atEnd = false;
        contentOuter.classList.remove("dashboard-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        contentOuter.classList.remove("dashboard-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        contentOuter.classList.add("dashboard-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        contentOuter.classList.remove("dashboard-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      contentOuter.classList.remove("dashboard-hscroll-end");
    };
  }, [isMobile, displayedProducts]);

  const handleLoading = useCallback(async () => {
    try {
      await dispatch(setAppLoading(isLoading || isFetching));
    } catch (error) {
      console.error("Error setting app loading state:", error);
    }
  }, [isLoading, isFetching, dispatch]);

  useEffect(() => {
    handleLoading();
  }, [handleLoading]);

  const refetchDashboardData = useCallback(async () => {
    try {
      await dispatch(setHeaderText("Dashboard"));
      await refetchRecentInvoice();
    } catch (error) {
      console.error("Error while refetching dashboard data:", error);
    }
  }, [dispatch, refetchRecentInvoice]);

  useEffect(() => {
    refetchDashboardData();
  }, [refetchDashboardData, dashboradDate, dashboradCurrency, currencySum, token, user]);

  const renderAmount = useCallback((rowData) => {
    try {
      if (!rowData || typeof rowData.inv_total_amount !== "number" || !rowData.inv_currency) {
        return <span>--</span>;
      }
      return (
        <span>
          {rowData.inv_total_amount.toLocaleString("en-US", {
            style: "currency",
            currency: rowData.inv_currency,
          })}
        </span>
      );
    } catch {
      return <span>--</span>;
    }
  }, []);

  const showUserCol = user && user.userType !== 3 && user.userType !== 5 && user.userType !== 4 && user.userType !== 6 && user.userType !== 7;

  const componentRef = useRef(null);
  const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } };

  return (
    <div className="invoice-outer">
      {!isMobile && (
        <div className="recent-invoice-title">
          {global.config.locate(appSettings && appSettings.language, "Recent Invoices")}
        </div>
      )}
      <div className="card">
        {isMobile && (
          <div className="recent-invoice-title-mobile">
            {global.config.locate(appSettings && appSettings.language, "Recent Invoices")}
          </div>
        )}
        {isLoading && products.length === 0 ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "160px" }}>
            <ThreeDots height="60" width="60" radius="9" color="#6479f9" ariaLabel="loading" />
          </div>
        ) : (
          <motion.div
            ref={componentRef}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.3 }}
          >
            <div className="time-table" ref={timeTableRef}>
              <table style={{ minWidth: isMobile ? "auto" : "50rem", width: "100%", textAlign: "left" }}>
                <thead>
                  {isMobile ? (
                    <tr>
                      <th className="sticky-col" onClick={() => handleSort("inv_number")}>
                        <div className="mobile-header-cell-inner">
                          <span>{global.config.locate(appSettings && appSettings.language, "Invoice ID")}</span>
                          <MobileSortIcon />
                        </div>
                      </th>
                      {showUserCol && (
                        <th onClick={() => handleSort("inv_for")}>
                          <div className="mobile-header-cell-inner">
                            <span>{global.config.locate(appSettings && appSettings.language, "User Name")}</span>
                            <MobileSortIcon />
                          </div>
                        </th>
                      )}
                      <th onClick={() => handleSort("inv_due_date")}>
                        <div className="mobile-header-cell-inner">
                          <span>{global.config.locate(appSettings && appSettings.language, "Due Date")}</span>
                          <MobileSortIcon />
                        </div>
                      </th>
                      <th onClick={() => handleSort("inv_payment_status")}>
                        <div className="mobile-header-cell-inner">
                          <span>{global.config.locate(appSettings && appSettings.language, "Payment Status")}</span>
                          <MobileSortIcon />
                        </div>
                      </th>
                      <th onClick={() => handleSort("inv_total_amount")}>
                        <div className="mobile-header-cell-inner">
                          <span>{global.config.locate(appSettings && appSettings.language, "Amount")}</span>
                          <MobileSortIcon />
                        </div>
                      </th>
                    </tr>
                  ) : (
                    <tr>
                      <th style={{ paddingRight: "44px" }} onClick={() => handleSort("inv_number")}>
                        <span style={sortableThStyle}>
                          {global.config.locate(appSettings && appSettings.language, "Invoice ID")}
                          <SortIcon field="inv_number" sortField={sortField} sortOrder={sortOrder} />
                        </span>
                      </th>
                      <th style={{ paddingRight: "44px" }} onClick={() => handleSort("inv_due_date")}>
                        <span style={sortableThStyle}>
                          {global.config.locate(appSettings && appSettings.language, "Due Date")}
                          <SortIcon field="inv_due_date" sortField={sortField} sortOrder={sortOrder} />
                        </span>
                      </th>
                      {showUserCol && (
                        <th style={{ paddingRight: "44px" }}>
                          {global.config.locate(appSettings && appSettings.language, "User Name")}
                        </th>
                      )}
                      <th style={{ paddingRight: "44px" }} onClick={() => handleSort("inv_payment_status")}>
                        <span style={sortableThStyle}>
                          {global.config.locate(appSettings && appSettings.language, "Payment Status")}
                          <SortIcon field="inv_payment_status" sortField={sortField} sortOrder={sortOrder} />
                        </span>
                      </th>
                      <th style={{ paddingRight: "44px" }} onClick={() => handleSort("inv_total_amount")}>
                        <span style={sortableThStyle}>
                          {global.config.locate(appSettings && appSettings.language, "Amount")}
                          <SortIcon field="inv_total_amount" sortField={sortField} sortOrder={sortOrder} />
                        </span>
                      </th>
                      <th style={{ paddingRight: "20px" }} />
                    </tr>
                  )}
                </thead>
                <tbody>
                  {displayedProducts.length === 0 ? (
                    <tr>
                      <td colSpan={showUserCol ? 6 : 5}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: "10px" }}>
                          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
                            <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
                            <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
                            <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No invoices found</p>
                          <p style={{ margin: 0, fontSize: "13px", color: "#96A8BA" }}>Create an invoice to get started.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    displayedProducts.map((row) => (
                      <tr key={row._id}>
                        {isMobile ? (
                          <>
                            <td className="sticky-col">
                              <Link to={`../invoice/view/${row._id}`} className="invoice-id-cell" style={{ textDecoration: 'none', color: '#1E293B', background: 'transparent', backgroundColor: 'transparent', border: 'none', borderRadius: 0, padding: 0, margin: 0, boxShadow: 'none' }}>
                                <span className="inv-num-text">{row.inv_number}</span>
                                <span className="mobile-chevron-icon">
                                  <ItemIcon />
                                </span>
                              </Link>
                            </td>
                            {showUserCol && (
                              <td className="user-name-cell">
                                <MemberName userId={row.inv_for} />
                              </td>
                            )}
                            <td>
                              {row.inv_due_date ? moment(row.inv_due_date).format("MMMM D, YYYY") : "--"}
                            </td>
                            <td>
                              <PaymentStatusWise
                                inv_payment_details={row.inv_payment_details}
                                invData={row}
                                invoiceId={row.inv_number}
                                createdAt={row.created_at}
                                paymentStatus={row.inv_payment_status}
                              />
                            </td>
                            <td className="bold-td">
                              {renderAmount(row)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ paddingRight: "44px" }} className="invoice-id-cell">
                              <span className="inv-num-text">{row.inv_number}</span>
                              <span className="mobile-chevron-icon">
                                <ItemIcon />
                              </span>
                            </td>
                            <td style={{ paddingRight: "44px" }}>
                              {row.inv_due_date ? moment(row.inv_due_date).format("MMMM D, YYYY") : "--"}
                            </td>
                            {showUserCol && (
                              <td style={{ paddingRight: "44px" }}>
                                <MemberName userId={row.inv_for} />
                              </td>
                            )}
                            <td style={{ paddingRight: "44px" }}>
                              <PaymentStatusWise
                                inv_payment_details={row.inv_payment_details}
                                invData={row}
                                invoiceId={row.inv_number}
                                createdAt={row.created_at}
                                paymentStatus={row.inv_payment_status}
                              />
                            </td>
                            <td style={{ paddingRight: "44px" }} className="bold-td">
                              {renderAmount(row)}
                            </td>
                            <td style={{ paddingRight: "20px" }}>
                              {renderCTA(row)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RecentInvoices;
